
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from './components/theme/ThemeProvider';
import { AuthProvider } from './context/AuthContext';
import { AnimationProvider } from './context/AnimationContext';
import { Toaster } from '@/components/ui/toaster';
import { ModuleErrorBoundary } from '@/shared/infrastructure/ErrorBoundary';
import { Suspense, useEffect } from 'react';
import SEOOptimizer from '@/components/seo/SEOOptimizer';
import ServiceWorker from '@/components/seo/ServiceWorker';
import { initializeAnalytics, trackCoreWebVitals, trackUserEngagement } from '@/lib/analytics';

// Lazy load components
import Index from './pages/Index';
import Home from './pages/Home';
import SignUp from './pages/SignUp';
import Login from './pages/Login';
import PersonalityTest from './pages/PersonalityTest';
import PersonalityTests from './pages/PersonalityTests';
import YoungSchemasTest from './pages/YoungSchemasTest';
import Registration from './pages/Registration';
import EmotionDashboard from './pages/EmotionDashboard';
import Profile from './pages/Profile';
import History from './pages/History';
import Feedback from './pages/Feedback';
import NotFound from './pages/NotFound';
import Settings from './pages/Settings';
import VoiceAgent from './pages/VoiceAgent';
import WorkPerformance from './pages/WorkPerformance';
import GuidedMeditation from './pages/GuidedMeditation';
import Community from './pages/Community';
import Journeys from './pages/Journeys';
import JourneyDetail from './pages/JourneyDetail';
import SessionDetail from './pages/SessionDetail';
import TestHistory from './pages/TestHistory';
import AdvancedAnalysis from './pages/AdvancedAnalysis';
import Referrals from './pages/Referrals';
import Corporate from './pages/Corporate';
import CorporateDashboard from './pages/CorporateDashboard';
import CorporatePersonality from './pages/CorporatePersonality';
import MentalHealthCompliance from './pages/MentalHealthCompliance';
import Termos from './pages/Termos';
import Privacidade from './pages/Privacidade';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import AvisoIA from './pages/AvisoIA';
import DesignSystem from './pages/DesignSystem';
import SeshDebug from './pages/SeshDebug';
import PersonalityTestHistory from './pages/PersonalityTestHistory';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (previously cacheTime)
    },
  },
});

function AppContent() {
  useEffect(() => {
    // Initialize Google Analytics - replace with your actual measurement ID
    const GA_MEASUREMENT_ID = 'G-XXXXXXXXXX';
    
    // Only initialize analytics in production
    if (process.env.NODE_ENV === 'production') {
      initializeAnalytics(GA_MEASUREMENT_ID);
      trackCoreWebVitals();
      trackUserEngagement();
    }
  }, []);

  return (
    <Router>
      <SEOOptimizer>
        <ModuleErrorBoundary>
          <Suspense fallback={<div>Loading...</div>}>
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<Index />} />
              <Route path="/cadastro" element={<SignUp />} />
              <Route path="/login" element={<Login />} />
              <Route path="/termos" element={<Termos />} />
              <Route path="/privacidade" element={<Privacidade />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/aviso-ia" element={<AvisoIA />} />

              {/* Protected routes */}
              <Route path="/app" element={<Home />} />
              <Route path="/perfil" element={<Profile />} />
              <Route path="/registro" element={<Registration />} />
              <Route path="/emocoes" element={<EmotionDashboard />} />
              <Route path="/desempenho" element={<WorkPerformance />} />
              <Route path="/agente-voz" element={<VoiceAgent />} />
              <Route path="/meditacoes" element={<GuidedMeditation />} />
              <Route path="/community" element={<Community />} />
              <Route path="/jornadas" element={<Journeys />} />
              <Route path="/jornadas/:id" element={<JourneyDetail />} />
              <Route path="/sessoes/:id" element={<SessionDetail />} />
              <Route path="/historico" element={<History />} />
              <Route path="/feedback" element={<Feedback />} />
              <Route path="/configuracoes" element={<Settings />} />
              <Route path="/testes-personalidade" element={<PersonalityTests />} />
              <Route path="/testes-personalidade/big-five" element={<PersonalityTest />} />
              <Route path="/testes-personalidade/young-schemas" element={<YoungSchemasTest />} />
              <Route path="/historico-testes" element={<TestHistory />} />
              <Route path="/historico-personalidade" element={<PersonalityTestHistory />} />
              <Route path="/analise-avancada" element={<AdvancedAnalysis />} />
              <Route path="/indicacoes" element={<Referrals />} />
              <Route path="/debug-seshs" element={<SeshDebug />} />

              {/* Corporate routes */}
              <Route path="/corporativo" element={<Corporate />} />
              <Route path="/corporativo/dashboard" element={<CorporateDashboard />} />
              <Route path="/corporativo/personalidade" element={<CorporatePersonality />} />
              <Route path="/corporativo/compliance" element={<MentalHealthCompliance />} />

              {/* Design System (development only) */}
              <Route path="/design-system" element={<DesignSystem />} />

              {/* 404 */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </ModuleErrorBoundary>
        <Toaster />
        <ServiceWorker />
      </SEOOptimizer>
    </Router>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="light" storageKey="metrocalma-ui-theme">
        <AuthProvider>
          <AnimationProvider>
            <AppContent />
          </AnimationProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
